package com.eladfinish.bible.contest.screens.names.ui

import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.extensions.capitalizeWord
import com.eladfinish.bible.contest.internet_is_required
import com.eladfinish.bible.contest.no_results_found
import com.eladfinish.bible.contest.screens.names.NamesAction
import com.eladfinish.bible.contest.screens.names.NamesEffect
import com.eladfinish.bible.contest.screens.names.NamesStore
import com.eladfinish.bible.contest.screens.names.data.Gender
import com.eladfinish.bible.contest.screens.names.data.InternalNameLink
import com.eladfinish.bible.contest.screens.names.data.NameItem
import com.eladfinish.bible.contest.screens.names.data.NameReference
import com.eladfinish.bible.contest.screens.names.ui.dialogs.NamesDialogs
import com.eladfinish.bible.contest.screens.names.utils.LinkInfoProvider
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.common.VerticalSpacer
import com.eladfinish.bible.contest.ui.components.HorizontalScrollbarContainer
import com.eladfinish.bible.contest.ui.components.RightToLeftRow
import com.eladfinish.bible.contest.ui.components.buttons.ItemButtonText
import com.eladfinish.bible.contest.ui.components.buttons.PrimaryButtonText
import com.eladfinish.bible.contest.ui.components.mouseWheelHorizontalScroll
import com.eladfinish.bible.contest.ui.components.simpleVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.horizontalFadingEdge
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.stringResource
import io.github.aakira.napier.Napier
import kotlinx.coroutines.launch
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun NamesScreen(
    store: NamesStore = koinViewModel(),
    showToastStringResource: (message: StringResource) -> Unit,
) {
    val listState = rememberLazyListState()
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect {
            Napier.d("SideEffect_ collect $it")

            when (it) {
                NamesEffect.NoInternet -> {
                    showToastStringResource(SharedRes.strings.internet_is_required)
                }

                NamesEffect.NoNameFound -> {
                    showToastStringResource(SharedRes.strings.no_results_found)
                }

                NamesEffect.ItemsLoaded -> {
                    coroutineScope.launch {
                        listState.animateScrollToItem(index = 0)
                    }
                }

                is NamesEffect.ScrollToItem -> {
                    coroutineScope.launch {
                        listState.animateScrollToItem(index = it.index)
                    }
                }
            }
        }
    }

    val namesState by store.observeState().collectAsState()

    NamesScreenContent(
        listState = listState,
        annotatedString = namesState.annotatedString,
        nameList = namesState.nameList,
        selectedGender = namesState.selectedGender,
        selectedLetter = namesState.selectedLetter,
        genderList = namesState.genderList,
        alephBetList = namesState.alephBetAsList,
        letterLines = namesState.letterLines,
        onGenderClick = {
            store.dispatch(NamesAction.GenderSelected(gender = it))
        },
        onLetterClick = {
            store.dispatch(NamesAction.LetterSelected(letter = it))
        },
        onItemCopy = {
            store.dispatch(NamesAction.OnItemCopied(text = it))
        },
        onItemBrowse = { name, nameReferences ->
            store.dispatch(NamesAction.BrowseSelected(name = name, nameReferences = nameReferences))
        },
        onInternalLinkClicked = {
            store.dispatch(NamesAction.InternalLinkClicked(internalNameLink = it))
        }
    )

    SetScreenIsLoading(namesState.isLoading)

    NamesDialogs(
        dialogState = namesState.dialogState,
        onAction = store::dispatch,
    )
}

@Composable
internal fun NamesScreenContent(
    listState: LazyListState,
    annotatedString: AnnotatedString,
    nameList: List<NameItem>,
    selectedGender: Gender,
    selectedLetter: Char?,
    genderList: List<Gender>,
    alephBetList: List<Char>,
    letterLines: Int,
    onGenderClick: (Gender) -> Unit,
    onLetterClick: (Char) -> Unit,
    onItemBrowse: (String, List<NameReference>) -> Unit,
    onItemCopy: (String) -> Unit,
    onInternalLinkClicked: (internalNameLink: InternalNameLink) -> Unit,
) {
    if (isShortWideWindow()) {
        // The letters beside the names: above them, they would leave the names about a line and a half.
        Row(modifier = Modifier.fillMaxSize()) {
            Column(
                modifier = Modifier
                    .weight(0.42f)
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState()),
            ) {
                GenderRow(
                    genderList = genderList,
                    selectedGender = selectedGender,
                    onGenderClick = onGenderClick,
                )

                VerticalSpacer()

                LetterRow(
                    alephBetList = alephBetList,
                    letterLines = LETTER_LINES_IN_SHORT_WINDOW,
                    selectedLetter = selectedLetter,
                    onLetterClick = onLetterClick,
                )
            }
            Box(modifier = Modifier.weight(0.58f).fillMaxHeight()) {
                NameList(
                    listState = listState,
                    annotatedString = annotatedString,
                    selectedLetter = selectedLetter ?: ' ',
                    nameList = nameList,
                    onItemCopy = onItemCopy,
                    onItemBrowse = onItemBrowse,
                    onInternalLinkClicked = onInternalLinkClicked,
                )
            }
        }
        return
    }

    Column(
        modifier = Modifier
            .fillMaxSize(),
    ) {
        GenderRow(
            genderList = genderList,
            selectedGender = selectedGender,
            onGenderClick = onGenderClick,
        )

        VerticalSpacer()

        LetterRow(
            alephBetList = alephBetList,
            letterLines = letterLines,
            selectedLetter = selectedLetter,
            onLetterClick = onLetterClick,
        )

        VerticalSpacer()

        NameList(
            listState = listState,
            annotatedString = annotatedString,
            selectedLetter = selectedLetter ?: ' ',
            nameList = nameList,
            onItemCopy = onItemCopy,
            onItemBrowse = onItemBrowse,
            onInternalLinkClicked = onInternalLinkClicked,
        )
    }
}

private const val LETTER_LINES_IN_SHORT_WINDOW = 4

@Composable
private fun NameList(
    listState: LazyListState,
    annotatedString: AnnotatedString,
    selectedLetter: Char,
    nameList: List<NameItem>,
    onItemBrowse: (String, List<NameReference>) -> Unit,
    onItemCopy: (String) -> Unit,
    onInternalLinkClicked: (internalNameLink: InternalNameLink) -> Unit,
) {

    val linkInfoProvider = LinkInfoProvider()

    LazyColumn(
        modifier = Modifier
            .simpleVerticalScrollbar(state = listState, color = Color.Gray)
            .fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        state = listState,
        contentPadding = navigationBarContentPadding(),
    ) {
        item {
            ResultTitle(annotatedString)
        }
        itemsIndexed(nameList) { index, nameData ->
            NameItemUi(
                index = index.plus(1),
                letter = selectedLetter,
                name = nameData.name,
                description = nameData.description,
                nameReferenceList = nameData.nameReferenceList,
                onItemBrowse = onItemBrowse,
                onItemCopy = onItemCopy,
                linkifyText = { letter, name, description ->
                    LinkifyText(
                        linkInfoProvider = linkInfoProvider,
                        letter = letter,
                        name = name,
                        text = description,
                        clickable = true,
                        fontSize = 16.sp,
                        modifier = Modifier.padding(
                            vertical = 4.dp,
                            horizontal = 5.dp,
                        ),
                        textAlign = TextAlign.Start,
                        style = MaterialTheme.typography.bodyLarge.copy(
                            textAlign = TextAlign.Center,
                            color = Color.Black,
                            textDirection = TextDirection.ContentOrRtl,
                        ),
                        onInternalLinkClicked = onInternalLinkClicked,
                    )
                }
            )
        }
        item {
            Spacer(modifier = Modifier.height(5.dp))
        }
    }
}

@Composable
private fun ResultTitle(
    annotatedString: AnnotatedString,
) {
    Text(
        text = annotatedString,
        style = MaterialTheme.typography.bodyLarge.copy(
            textAlign = TextAlign.Center,
            color = Color.DarkGray,
            textDirection = TextDirection.Content,
        ),
    )
}

@Composable
private fun GenderRow(
    genderList: List<Gender>,
    selectedGender: Gender,
    onGenderClick: (Gender) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 10.dp),
        horizontalArrangement = Arrangement.Center,
    ) {
        genderList.forEach { gender ->
            PrimaryButtonText(
                modifier = Modifier
                    .weight(1f)
                    .padding(5.dp),
                text = stringResource(gender.stringResource).capitalizeWord(),
                borderWidth = if (gender == selectedGender) 3 else 0,
                onClick = {
                    onGenderClick.invoke(gender)
                },
            )
        }
    }
}

@Composable
private fun LetterRow(
    alephBetList: List<Char>,
    letterLines: Int,
    selectedLetter: Char?,
    onLetterClick: (Char) -> Unit,
) {
    // alephBetList comes from a localized string resource; on Kotlin/JS moko-resources'
    // StringDesc.localized() resolves asynchronously, so the very first composition can see an
    // empty (or otherwise shorter-than-letterLines) list before the real value loads and
    // recomposes. Render nothing for that one frame rather than dividing by a chunk size of 0.
    if (alephBetList.size < letterLines) return

    val alephBetListMultiline = alephBetList.chunked(alephBetList.size / letterLines)
    val state = rememberScrollState()

    val count = alephBetListMultiline.maxByOrNull { it.size }?.size ?: 0

    HorizontalScrollbarContainer(
        scrollState = state,
        layoutDirection = LayoutDirection.Rtl,
    ) {
        RightToLeftRow(
            modifier = Modifier
                .horizontalFadingEdge(
                    scrollState = state,
                    length = 20.dp,
                    edgeColor = Color.Black,
                )
                .mouseWheelHorizontalScroll(state)
                .horizontalScroll(state)
        ) {
            repeat(count) { letterIndex ->
                Column {
                    // The letters do not always divide evenly: the last line can be shorter.
                    alephBetListMultiline.forEach { line ->
                        val letter = line.getOrNull(letterIndex) ?: return@forEach
                        ItemButtonText(
                            modifier = Modifier
                                .padding(1.dp),
                            text = letter.toString(),
                            borderWidth = if (letter == selectedLetter) 3 else 0,
                            onClick = {
                                onLetterClick.invoke(letter)
                            },
                        )
                    }
                }
            }
        }
    }
}
